"use client";

/**
 * CopytradingSkipBanner — silent-failure detector for copytrading.
 *
 * Why this exists: production audit (2026-05-28) found two top-volume
 * users (toddgarber, keypi033) trading for 7+ days at <20% fill rate
 * without ever seeing a diagnostic alert, because they had disabled
 * `notify_copy_trade` to escape the success-confirmation spam. Mig 272
 * split that toggle into success vs skip channels, but a paranoid user
 * can still mute everything. This banner is the inescapable nudge that
 * survives all notification preferences — it sits in-app on every page
 * and reads execution outcomes directly from the DB, so the user can't
 * silence the signal without addressing the underlying config issue.
 *
 * Show condition: skipped24h > 50 AND fillRatePct < 20 (or null when
 * filled24h is zero). Dismiss persists 24h in localStorage; if the
 * condition still holds at the 24h boundary, the banner re-appears.
 * If the user fixes their config (raises slippage, deposits, etc.)
 * the next day's skip rate drops and the banner self-heals — they
 * never see it again.
 *
 * UI mirrors EoaSweepBanner / DepositWrapBanner: fixed bottom-right
 * card, amber accent (warn, not error), one CTA + dismiss X. Mounted
 * once in AppLayoutWithSidebar.
 */

import { useEffect, useState } from "react";
import Link from "next/link";
import { AlertTriangle, X as XIcon } from "lucide-react";
import { authFetch } from "@/lib/authFetch";
import { useAuth } from "@/hooks/useAuth";
import { useT } from "@/hooks/useT";
import { createLogger } from "@/lib/logger";

// Locale-bound translate fn signature (matches useT()'s return).
type TFn = (key: string, vars?: Record<string, string | number>) => string;

const logger = createLogger("CopytradingSkipBanner");

// localStorage key for the 24h dismiss timestamp. Stored as the
// epoch-ms when the dismiss window expires, so a stale entry from a
// previous session auto-expires on the next mount without us needing
// any reaper logic.
const DISMISS_KEY = "copytrading_skip_banner_dismissed_until";
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000;

// Heuristic thresholds. Tuned to catch the two production cases
// (toddgarber + keypi033) without false-positives for users whose
// pain is wallet state, market conditions, or working-as-intended
// caps.
//
// Gate stack (all required):
//   1. SKIP_THRESHOLD: ≥50 skips+fails in 24h — actively copytrading,
//      not a casual visitor.
//   2. FIXABLE_CODES: the dominant error_code must be a config issue
//      the user can fix by editing copy settings. Wallet-empty,
//      capped-out-on-purpose, market-state, and backend-bug codes
//      are excluded so the banner doesn't fire for problems where
//      "Open copy settings" is the wrong CTA. The existing
//      CopyPausedBanner / LowBalance notif / auto-pause flow handle
//      those cases on their own dedicated channels.
//   3. Either:
//        a) fillRate < FILL_RATE_THRESHOLD (overall waste), OR
//        b) the fixable top reason concentrates ≥ TOP_REASON_SHARE
//           of all skips (clear single root cause — keypi033's case:
//           114/137 = 83% MARKET_ODDS_TOO_HIGH at 26% fill rate
//           would slip past a fill-rate-only gate).
const SKIP_THRESHOLD = 50;
const FILL_RATE_THRESHOLD = 30;
const TOP_REASON_SHARE = 0.5;

// Error codes where the fix is "edit your copy settings". Other
// codes (INSUFFICIENT_BALANCE, MAX_OPEN_POSITIONS, LEADER_DISABLED,
// PHANTOM_FILL_NOT_FOUND, etc.) are intentionally excluded — they
// either have their own dedicated UI signal already, are working as
// the user explicitly configured, or are backend issues that
// pointing the user at copy settings can't address.
const FIXABLE_CODES = new Set<string>([
  "SLIPPAGE_EXCEEDED",
  "SLIPPAGE_EXCEEDED_FINAL",
  "HIGH_SLIPPAGE",
  "COPY_SIZE_BELOW_MIN",
  "COPY_SIZE_ABOVE_MAX",
  "MARKET_ODDS_TOO_HIGH",
  "MARKET_ODDS_TOO_LOW",
  "MAX_ENTRIES_PER_MARKET",
]);

// Re-fetch every 5 min. A user mid-session who fixes their config
// (deposits funds, raises slippage cap, etc.) should see the banner
// disappear within 5 minutes — fast enough to feel responsive,
// slow enough to not hammer the backend.
const POLL_INTERVAL_MS = 5 * 60 * 1000;

interface SkipStats {
  filled24h: number;
  skipped24h: number;
  failed24h: number;
  fillRatePct: number | null;
  topSkipReason: { code: string; count: number } | null;
}

// Friendly mapping for the most common skip codes → i18n key. Falls back
// to the raw code (lowercased + spaced) when we don't have a custom string.
const REASON_KEY: Record<string, string> = {
  SLIPPAGE_EXCEEDED: "skipBanner.reason.slippage",
  SLIPPAGE_EXCEEDED_FINAL: "skipBanner.reason.slippage",
  HIGH_SLIPPAGE: "skipBanner.reason.slippage",
  COPY_SIZE_BELOW_MIN: "skipBanner.reason.sizeBelowMin",
  BELOW_MIN_TRADE_SIZE: "skipBanner.reason.belowMinTrade",
  COPY_SIZE_ABOVE_MAX: "skipBanner.reason.sizeAboveMax",
  INSUFFICIENT_BALANCE: "skipBanner.reason.insufficientBalance",
  MARKET_ODDS_TOO_HIGH: "skipBanner.reason.oddsHigh",
  MARKET_ODDS_TOO_LOW: "skipBanner.reason.oddsLow",
  PHANTOM_FILL_NOT_FOUND: "skipBanner.reason.phantom",
  ORDER_NEVER_FILLED: "skipBanner.reason.neverFilled",
  ORDER_NO_MATCH: "skipBanner.reason.noMatch",
  MAX_DAILY_VOLUME: "skipBanner.reason.maxDailyVolume",
  MAX_OPEN_POSITIONS: "skipBanner.reason.maxOpenPositions",
  MAX_ENTRIES_PER_MARKET: "skipBanner.reason.maxEntries",
};

function labelFor(t: TFn, code: string): string {
  const key = REASON_KEY[code];
  return key ? t(key) : code.toLowerCase().replace(/_/g, " ");
}

export default function CopytradingSkipBanner() {
  const { isLoggedIn, isReady } = useAuth();
  const t = useT();
  const [stats, setStats] = useState<SkipStats | null>(null);
  const [dismissed, setDismissed] = useState<boolean>(true); // Default true so banner doesn't flash before localStorage check

  // Re-read the dismissed-until timestamp on mount and any time the
  // poll fires, so a stale localStorage entry from yesterday is
  // automatically forgotten without needing any cleanup logic.
  const checkDismissed = (): boolean => {
    try {
      const raw = typeof window !== "undefined" ? localStorage.getItem(DISMISS_KEY) : null;
      if (!raw) return false;
      const until = Number(raw);
      if (!Number.isFinite(until)) return false;
      return Date.now() < until;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    setDismissed(checkDismissed());
  }, []);

  useEffect(() => {
    if (!isReady || !isLoggedIn) return;

    let cancelled = false;

    const fetchStats = async () => {
      try {
        const res = await authFetch("/api/copytrading/skip-stats");
        if (!res.ok || cancelled) return;
        const json = (await res.json()) as SkipStats;
        if (!cancelled) {
          setStats(json);
          // Re-check dismissed on every poll so a 24h window that
          // expired mid-session lets the banner come back.
          setDismissed(checkDismissed());
        }
      } catch (err) {
        // Silent — banner is opportunistic; a failed poll just means
        // we'll try again in 5 min.
        logger.debug("Failed to fetch skip stats", { error: err instanceof Error ? err.message : String(err) });
      }
    };

    fetchStats();
    const id = setInterval(fetchStats, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [isReady, isLoggedIn]);

  const handleDismiss = () => {
    try {
      const until = Date.now() + DISMISS_DURATION_MS;
      localStorage.setItem(DISMISS_KEY, String(until));
    } catch {
      // Storage disabled — at least hide for the current session.
    }
    setDismissed(true);
  };

  if (!isReady || !isLoggedIn || dismissed || !stats) return null;

  const skipped = stats.skipped24h + stats.failed24h;
  const lowFillRate = stats.fillRatePct === null || stats.fillRatePct < FILL_RATE_THRESHOLD;
  const topReasonShare = stats.topSkipReason && skipped > 0
    ? stats.topSkipReason.count / skipped
    : 0;
  const concentratedFailure = topReasonShare >= TOP_REASON_SHARE;
  // Gate: top reason must be config-fixable. Anything else
  // (INSUFFICIENT_BALANCE / MAX_OPEN_POSITIONS / LEADER_DISABLED /
  // PHANTOM_FILL_NOT_FOUND / etc.) is handled by its own dedicated
  // surface (CopyPausedBanner, LowBalance notif, etc.) — pointing
  // the user at copy settings here would be the wrong CTA.
  const topIsFixable = !!(stats.topSkipReason && FIXABLE_CODES.has(stats.topSkipReason.code));
  const meetsThreshold =
    skipped >= SKIP_THRESHOLD
    && topIsFixable
    && (lowFillRate || concentratedFailure);
  if (!meetsThreshold) return null;

  const reasonLabel = stats.topSkipReason ? labelFor(t, stats.topSkipReason.code) : null;
  const fillPctText = stats.fillRatePct !== null ? `${stats.fillRatePct}%` : "0%";

  return (
    <div
      className="fixed z-[400] bottom-4 right-4 max-w-[360px] bg-white dark:bg-[#18181D] border border-amber-500/40 rounded-[8px] shadow-lg pointer-events-auto"
      role="alert"
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="relative w-9 h-9 rounded-[6px] bg-amber-500/10 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
          </div>
          <div className="flex-1 min-w-0 pr-1">
            <p className="text-[13px] font-semibold text-gray-900 dark:text-white leading-snug mb-0.5">
              {t("skipBanner.title")}
            </p>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-snug">
              {t("skipBanner.body", { pct: fillPctText, total: skipped + stats.filled24h })}
              {reasonLabel
                ? " " + t("skipBanner.topReason", { reason: reasonLabel, count: stats.topSkipReason!.count })
                : null}
            </p>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label={t("skipBanner.dismissAria")}
            className="p-1 rounded-[4px] text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5 transition-colors flex-shrink-0"
          >
            <XIcon className="w-3.5 h-3.5" />
          </button>
        </div>
        <Link
          href="/copytrading"
          className="mt-3 inline-flex items-center justify-center w-full px-3 py-2 rounded-[5px] text-xs font-semibold bg-[#CCFF00] text-black hover:bg-[#B8E600] transition-colors"
        >
          {t("skipBanner.cta")}
        </Link>
        <p className="mt-2 text-[10px] text-gray-400 dark:text-gray-500 text-center">
          {t("skipBanner.dismissNote")}
        </p>
      </div>
    </div>
  );
}
